import React from 'react';
import {styled} from '@mui/material/styles';
import PoweredByAmbitionBox from '../PoweredByAB';
import {addUtmParamsToUrl, getNestedProperty} from '@jobseeker/lotus/utils';
import {Image} from '@jobseeker/lotus/components/Image';
import {RoundedStar, StarHirist} from '@jobseeker/lotus/icons';
import {Box, useMediaQuery} from '@mui/material';
import {TrackEventUBA} from '@jobseeker/common-core/utils';

const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const ReviewMainDiv = styled(Box)(() => ({}));

const ReviewContainer = styled(Box)(() => ({
  width: 'auto',
  height: 'auto',
  padding: '19px 10px',
  borderRadius: 16,
  boxShadow: '0px 6.48px 6px 2px rgba(41, 41, 41, 0.02);',
  backgroundColor: '#fff',
  position: 'relative',
  '& [data="powered-by-ambitionbox-footer"]': {
    justifyContent: 'flex-start',
    marginLeft: 5,
  },
  marginTop: useMediaQuery('(min-width:900px)') ? 0 : isAppIimjobs ? 16 : 8,
  marginBottom: useMediaQuery('(min-width:900px)') && !isAppIimjobs ? 24 : 0,
}));

const ReviewHeaderContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  marginBottom: isAppIimjobs ? 24 : useMediaQuery('(min-width:900px)') ? 8 : 0,
}));

const ReviewHeader = styled(Box)(({theme}) => ({
  fontFamily: isAppIimjobs ? 'poppins' : 'Nunito Sans',
  fontSize: 16,
  fontWeight: 600,

  color: 'var(--black)',
  [theme.breakpoints.between('md', 'lg')]: {
    maxWidth: isAppIimjobs ? 120 : 'auto',
    fontSize: isAppIimjobs ? 14 : 16,
  },
}));

const ViewAllReviews = styled('a')(() => ({
  fontFamily: isAppIimjobs ? 'poppins' : 'Nunito Sans',
  fontSize: 14,
  fontWeight: isAppIimjobs ? 500 : 600,
  color: isAppIimjobs ? '#49966E' : '#e96309',
  marginRight: isAppIimjobs ? 16 : 0,
  textDecoration: 'none',
  '&:hover': {
    color: isAppIimjobs ? '#49966E' : '#e96309',
  },
}));

const ReviewInfo = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '40px 1fr',
  fontFamily: isAppIimjobs ? 'poppins' : 'Nunito Sans',
  fontSize: 14,
  fontWeight: 500,
  color: 'var(--black)',
}));

const Review = styled(Box)(() => ({
  display: 'flex',
}));

const Rating = styled(Box)(() => ({
  fontFamily: isAppIimjobs ? 'Abhaya Libre' : 'Nunito Sans',
  fontSize: isAppIimjobs ? 16 : 14,
  lineHeight: '20px',
  fontWeight: 700,
  opacity: isAppIimjobs ? 1 : 0.7,
}));

const ReviewDesc = styled('a')(() => ({
  width: 'auto',
  height: 'auto',
  minHeight: '19px',
  margin: '0 0 12px 10px',
  fontFamily: isAppIimjobs ? 'poppins' : 'Nunito Sans',
  fontSize: 14,
  fontWeight: isAppIimjobs ? 500 : 'bold',
  color: 'var(--black)',
  textDecoration: 'none',
}));

const SeparatorDiv = styled(Box)(() => ({
  backgroundColor: '#f2f2f2',
  marginBottom: 12,
  height: '1px',
  marginLeft: -10,
  marginRight: -10,
}));

interface JobReview {
  rating: number;
  name: string;
  url: string;
  count?: number;
}

interface JobReviewData {
  entities: JobReview[];
  topJobProfilesUrl: string;
}

interface CompanyJobReviewsProps {
  jobReviewData: JobReviewData;
  companyName?: string;
  companyId?: number;
}

const CompanyJobReviews: React.FC<CompanyJobReviewsProps> = ({
  jobReviewData,
  companyId = 0,
  companyName = '',
}) => {
  const trackEvent = () => {
    TrackEventUBA('reviewByJobProfileClick', 'click', {
      page: 'Company Details Page',
      companyID: companyId || '',
      companyName: companyName || '',
      sessionUsername: sessionStorage.getItem('userId') || '',
    });
  };
  if ((getNestedProperty(jobReviewData, 'entities') || []).length === 0)
    return null;
  return (
    <ReviewMainDiv>
      {!isAppIimjobs ? (
        <ReviewHeaderContainer>
          <ReviewHeader>Reviews by Job Profile</ReviewHeader>
          <ViewAllReviews
            onClick={trackEvent}
            data-testid="view_all_reviews"
            title={`${companyName} Reviews`}
            target="_blank"
            rel="nofollow"
            href={addUtmParamsToUrl(
              jobReviewData.topJobProfilesUrl,
              'TOP_JOB_PROFILES',
              useMediaQuery('(min-width:900px)')
            )}
          >
            View All
          </ViewAllReviews>
        </ReviewHeaderContainer>
      ) : (
        <></>
      )}
      <ReviewContainer>
        {isAppIimjobs ? (
          <ReviewHeaderContainer>
            <ReviewHeader>Reviews by Job Profile</ReviewHeader>
            <ViewAllReviews
              data-testid="view_all_reviews"
              title={`${companyName} Reviews`}
              target="_blank"
              rel="nofollow"
              href={addUtmParamsToUrl(
                jobReviewData.topJobProfilesUrl,
                'TOP_JOB_PROFILES',
                useMediaQuery('(min-width:900px)')
              )}
            >
              View All
            </ViewAllReviews>
          </ReviewHeaderContainer>
        ) : (
          <></>
        )}

        {jobReviewData &&
          jobReviewData.entities &&
          jobReviewData.entities.slice(0, 5).map((review: any, index: any) => (
            <React.Fragment key={index}>
              <ReviewInfo data-testid={`ab_review_${index}`}>
                <Review>
                  <Image
                    height={isAppIimjobs ? 18 : 14}
                    width={isAppIimjobs ? 18 : 14}
                    src={isAppIimjobs ? RoundedStar : StarHirist}
                    alt="star"
                  />{' '}
                  &nbsp;
                  <Rating>{review.rating.toFixed(1)}</Rating>
                </Review>
                <ReviewDesc
                  title={`${companyName} ${review.name} Reviews`}
                  target="_blank"
                  rel="nofollow"
                  href={addUtmParamsToUrl(
                    review.url,
                    'TOP_JOB_PROFILES',
                    useMediaQuery('(min-width:900px)')
                  )}
                  onClick={trackEvent}
                >
                  {review.name}
                </ReviewDesc>
              </ReviewInfo>
              {index < 4 && <SeparatorDiv />}
            </React.Fragment>
          ))}
        <PoweredByAmbitionBox
          title={`${companyName} Reviews`}
          redirectionUrl={addUtmParamsToUrl(
            jobReviewData.topJobProfilesUrl,
            'TOP_JOB_PROFILES',
            useMediaQuery('(min-width:900px)')
          )}
        />
      </ReviewContainer>
    </ReviewMainDiv>
  );
};

export default CompanyJobReviews;
